import React, { createRef, useEffect, useState } from 'react';
import { OktaAuth } from '@okta/okta-auth-js';
import { useResendOtpMutation, useSignInMutation, useValidateSignInV2Mutation } from 'api/authApi';
import { AUTH_PROVIDERS, OKTA_CODE_CHALLENGE_METHOD } from 'constants/auth';
import { OKTA_LOGO } from 'constants/icons';
import mixpanelEvents from 'constants/mixpanel';
import { ROUTES_PATH } from 'constants/routeConfig';
import {
  Alert,
  Button,
  BUTTON_SIZE_TYPES,
  BUTTON_STATE_TYPES,
  BUTTON_TYPES,
  Input,
  SUPPORT_INFO_TYPES,
  SupporterInfo,
  SvgSpriteLoader,
} from 'destiny';
import { ICON_SPRITE_TYPES } from 'destiny/dist/constants/icons';
import { AlertTypes } from 'destiny/dist/constants/molecules/alert';
import { useFormik } from 'formik';
import { usePostLogin } from 'hooks/usePostLogin';
import LoginLayout from 'layout/login-layout';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { MapAny } from 'types';
import { ValidateSignInResponseType } from 'types/api';
import { getFromLocalStorage, LOCAL_STORAGE_KEYS } from 'utils/localstorage';
import { trackMixpanel } from 'utils/mixpanel';
import {
  getFromSessionStorage,
  removeFromSessionStorage,
  SESSION_STORAGE_KEYS,
  setToSessionStorage,
} from 'utils/sessionStorage';
import * as yup from 'yup';
import FullPageLoader from 'components/FullPageLoader';
import { ButtonLoader } from 'components/loader/ButtonLoader';
import OtpInputWithResend from 'components/OtpInputWithResend';

const emptyToast = {
  hidden: true,
  isError: false,
  message: '',
};

const Login: React.FC = () => {
  const cachedUserEmail = getFromLocalStorage(LOCAL_STORAGE_KEYS.XZAMP_USER);
  const cachedUserLoginToken = getFromSessionStorage(SESSION_STORAGE_KEYS.ZAMP_LOGIN_TOKEN);
  const [toast, setToast] = useState(emptyToast);
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [signInInfo, setSignInInfo] = useState<MapAny>({
    email: cachedUserEmail || '',
    token: cachedUserLoginToken || '',
    context: { provider: AUTH_PROVIDERS.DEFAULT },
  });
  const [otpError, setOtpError] = useState('');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const emailInputRef = createRef<HTMLInputElement>();

  const [signIn] = useSignInMutation();
  const [validateLoginV2] = useValidateSignInV2Mutation();
  const [resendOtp, { isLoading: isFetchingOtp }] = useResendOtpMutation();

  const router = useRouter();
  const { code } = router.query ?? {};

  const handlePostLogin = usePostLogin();

  useEffect(() => {
    trackMixpanel(mixpanelEvents.LOGIN_PAGE_LOAD, {});
  }, []);

  useEffect(() => {
    if (code) {
      handleValidateSso();
    }
  }, [code]);

  useEffect(() => {
    if (otp.replace(' ', '').length == 6) setOtpError('');
  }, [otp]);

  useEffect(() => {
    emailInputRef?.current?.focus();
  }, [emailInputRef?.current, otpSent]);

  const handleToast = (toastObject: any) => {
    setToast(toastObject);
  };

  const attemptLogin = async (values: any) => {
    trackMixpanel(mixpanelEvents.LOGIN_OTP_REQUEST, { email: emailValidationFormik.values.email });

    try {
      setIsLoading(true);
      const result = await signIn({
        email: values.email,
      }).unwrap();

      setSignInInfo({
        email: values?.email,
        token: result?.token,
        context: result?.context ?? { provider: AUTH_PROVIDERS.DEFAULT },
      });
      setToSessionStorage(SESSION_STORAGE_KEYS.ZAMP_LOGIN_TOKEN, result?.token);

      setOtpSent(true);
      setToast(emptyToast);
    } catch (e: any) {
      handleToast({
        hidden: false,
        isError: true,
        message: e?.data?.error?.message || 'Some error occured',
      });
    }
    setIsLoading(false);
  };

  const handleResendOtp = async () => {
    trackMixpanel(mixpanelEvents.LOGIN_OTP_RESEND, { email: emailValidationFormik.values.email });

    try {
      setToast(emptyToast);
      await resendOtp({
        token: signInInfo.token,
      }).unwrap();
      handleToast({
        hidden: false,
        isError: false,
        message: 'An OTP has been sent to your email',
      });
    } catch (e: any) {
      handleToast({
        hidden: false,
        isError: true,
        message: e?.data?.error?.message || 'Some error occured',
      });
    }
  };

  const handleOtpSubmit = async () => {
    removeFromSessionStorage(SESSION_STORAGE_KEYS.ZAMP_LOGIN_TOKEN);
    trackMixpanel(mixpanelEvents.LOGIN_SUBMIT, { email: emailValidationFormik.values.email });

    setToast(emptyToast);
    if (otp.replace(' ', '').length < 6) {
      setOtpError('Invalid OTP, please try again');
    } else {
      setOtpError('');
      try {
        setIsLoading(true);
        const response: ValidateSignInResponseType = await validateLoginV2({
          token: signInInfo?.token,
          code: otp,
        }).unwrap();

        trackMixpanel(mixpanelEvents.IDENTIFY, response);

        if (response?.id) handlePostLogin(response.id);
      } catch (e: any) {
        setOtpError(e?.data?.error?.message || 'Some error occured');
      }
      setIsLoading(false);
    }
  };

  const handleValidateSso = async () => {
    removeFromSessionStorage(SESSION_STORAGE_KEYS.ZAMP_LOGIN_TOKEN);
    setToast(emptyToast);
    try {
      setIsLoading(true);
      const response: ValidateSignInResponseType = await validateLoginV2({
        token: signInInfo?.token,
        code: code?.toString() ?? '',
      }).unwrap();

      if (response?.id) {
        handlePostLogin(response.id);
      } else {
        throw new Error('Invalid OTP');
      }
    } catch (e: any) {
      setToast({
        hidden: false,
        isError: true,
        message: e?.data?.error?.message || 'Something went wrong.',
      });
      setIsLoading(false);
      setSignInInfo({
        ...signInInfo,
        token: '',
        provider: AUTH_PROVIDERS.DEFAULT,
      });
      router.replace(ROUTES_PATH.LOGIN);
    }
  };

  const emailValidationSchema = yup.object().shape({
    email: yup.string().email('Please enter a valid email').required('Please enter your email'),
  });

  const emailValidationFormik = useFormik({
    initialValues: {
      email: cachedUserEmail ?? '',
    },
    validationSchema: emailValidationSchema,
    onSubmit: attemptLogin,
  });

  const renderEmailLogin = () => (
    <>
      <div className='tw-mt-9' data-testid='login-email-form'>
        <Input
          inputFieldProps={{
            inputTagProps: {
              placeHolder: '',
              name: 'email',
              onBlur: emailValidationFormik.handleBlur,
              value: emailValidationFormik.values.email,
              onChange: emailValidationFormik.handleChange,
              onKeyPress: (event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  emailValidationFormik.submitForm();
                }
              },
              inputRef: emailInputRef,
              error: emailValidationFormik.touched.email ? emailValidationFormik.errors.email : '',
              id: 'LOGIN_EMAIL_INPUT',
            },
            size: 'MEDIUM',
          }}
          labelProps={{
            title: 'Business email',
          }}
          showLabel
        />
        {emailValidationFormik.errors.email && emailValidationFormik.touched.email && (
          <SupporterInfo
            wrapperClass='tw-flex tw-items-center tw-mt-4'
            iconClass='tw-w-3 tw-h-3 tw-mr-2'
            icon='alert-triangle'
            iconAttributes={{
              id: 'alert-triangle',
              width: 12,
              height: 12,
              iconCategory: ICON_SPRITE_TYPES.ALERTS_AND_FEEDBACK,
            }}
            type={SUPPORT_INFO_TYPES.ERROR}
            text={emailValidationFormik.errors.email}
          />
        )}
      </div>
      <Button
        buttonProps={{
          size: BUTTON_SIZE_TYPES.MEDIUM,
          btnType: BUTTON_TYPES.PRIMARY,
          state: isLoading ? BUTTON_STATE_TYPES.DISABLED : BUTTON_STATE_TYPES.DEFAULT,
          isLoading: isLoading,
          wrapperClass: 'tw-mt-9 tw-w-24 tw-overflow-clip',
          onClick: () => emailValidationFormik.submitForm(),
          id: 'LOGIN_CONTINUE_BUTTON',
          eventCallback: (id, data) => trackMixpanel(id, { data, error: emailValidationFormik.errors }),
        }}
        loader={<ButtonLoader />}
      >
        Continue
      </Button>
    </>
  );

  const handleEditClick = () => {
    setOtpError('');
    setToast(emptyToast);
    setOtpSent(false);
    trackMixpanel(mixpanelEvents.EDIT_EMAIL_CTA);
  };

  const renderOtpScreen = () => {
    if (signInInfo?.context?.provider === AUTH_PROVIDERS.OKTA) {
      return renderSSOLogin();
    }

    return (
      <div className='tw-mt-9' data-testid='login-otp-wrapper'>
        <OtpInputWithResend
          className='tw-mt-4'
          value={otp}
          error={otpError}
          onChange={(value: string) => setOtp(value)}
          onResend={handleResendOtp}
          onSubmit={handleOtpSubmit}
          title=''
          subTitle={
            <span className='f-14-300' data-testid='login-otp-title'>
              OTP sent to <strong>{emailValidationFormik.values.email}</strong>
              <span
                className='f-14-500 tw-ml-2 tw-cursor-pointer tw-text-ZAMP_PRIMARY'
                onClick={handleEditClick}
                data-testid='login-otp-edit-button'
              >
                Edit
              </span>
            </span>
          }
          hideMinuteOnZero={true}
        />
        <Button
          buttonProps={{
            size: BUTTON_SIZE_TYPES.MEDIUM,
            btnType: BUTTON_TYPES.PRIMARY,
            state: isLoading || isFetchingOtp ? BUTTON_STATE_TYPES.DISABLED : BUTTON_STATE_TYPES.DEFAULT,
            isLoading: isLoading || isFetchingOtp,
            wrapperClass: 'tw-mt-9 tw-w-24 tw-overflow-clip',
            onClick: handleOtpSubmit,
            id: 'LOGIN_SUBMIT_OTP_BUTTON',
            eventCallback: trackMixpanel,
          }}
          loader={<ButtonLoader />}
        >
          Submit
        </Button>
      </div>
    );
  };

  const renderSSOLogin = () => {
    const oktaAuth = new OktaAuth({
      issuer: signInInfo?.context?.issuer,
      clientId: signInInfo?.context?.clientId,
      redirectUri: ROUTES_PATH.LOGIN,
      codeChallengeMethod: OKTA_CODE_CHALLENGE_METHOD,
      codeChallenge: signInInfo?.token,
    });

    return (
      <div className='tw-mt-12'>
        <div className='f-14-400 tw-mb-4'>Continue login with below SSO Providers</div>
        <Image
          src={OKTA_LOGO}
          width={80}
          height={30}
          alt='Login with Okta'
          onClick={() => oktaAuth.signInWithRedirect()}
          className='tw-shadow-md tw-px-4 tw-py-2 tw-pt-2.5 tw-rounded-2.5 hover:tw-bg-BASE_PRIMARY tw-cursor-pointer tw-border'
        />
      </div>
    );
  };

  if (code && isLoading) {
    return <FullPageLoader />;
  }

  return (
    <LoginLayout isTransition={otpSent}>
      <div
        data-testid='login-wrapper'
        className='tw-relative tw-h-screen tw-flex tw-flex-col tw-items-center tw-justify-center tw-w-full'
      >
        {!toast.hidden && (
          <Alert
            className='tw-self-stretch tw-absolute tw-top-20 f-12-300 tw-w-full !tw-justify-start'
            alertType={toast?.isError ? AlertTypes.ERROR : AlertTypes.PENDING}
            id='LOGIN_TOAST'
          >
            {toast?.isError && (
              <SvgSpriteLoader
                id='alert-triangle'
                iconCategory={ICON_SPRITE_TYPES.ALERTS_AND_FEEDBACK}
                width={12}
                height={12}
                className='tw-mr-2'
              />
            )}
            {toast.message}
          </Alert>
        )}
        <div data-testid='login-content-wrapper' className='tw-self-stretch -tw-mt-28 tw-transition-all'>
          {/* <div
            className={`f-14-500 tw-absolute tw-top-1 tw-border tw-px-4 tw-rounded-lg
            ${toast.hidden ? 'tw-invisible' : 'tw-visible'}
            ${
              toast.isError
                ? 'tw-bg-BG_ERROR_RED tw-text-ERROR_RED_2 tw-border-ERROR_RED'
                : 'tw-bg-green-50 tw-text-green-800 tw-border-green-600'
            }`}
          ></div> */}
          <div data-testid='login-title' className='f-24-500 tw-w-full tw-text-TEXT_GRAY_1'>
            Global business banking. Simplified
          </div>
          <div data-testid='login-subtitle' className='f-20-400 tw-w-full tw-text-TEXT_GRAY_1 tw-mt-9'>
            Login
          </div>
          {!otpSent ? renderEmailLogin() : renderOtpScreen()}
          <div data-testid='login-footer' className='tw-w-full tw-absolute tw-bottom-14 2xl_custom:tw-bottom-9'>
            <div
              data-testid='login-footer-wrapper'
              className='tw-w-[calc(100%-90px)] tw-relative tw-flex tw-justify-between tw-items-end  tw-text-TEXT_SECONDARY'
            >
              <div data-testid='login-footer-container' className='tw-flex tw-items-center'>
                <div data-testid='login-footer-title' className='f-12-300'>
                  Don&lsquo;t have an account ?
                </div>
                <div
                  data-testid='login-footer-button'
                  role='presentation'
                  onClick={() => trackMixpanel(mixpanelEvents.SIGNUP_CLICK, {})}
                >
                  <Link
                    className='f-16-400 tw-cursor-pointer tw-text-ZAMP_PRIMARY tw-ml-2'
                    href={ROUTES_PATH.SIGNUP}
                    data-testid='login-footer-button-sign-up'
                  >
                    Sign up
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </LoginLayout>
  );
};

export default Login;
